/**
 * Pure helpers for the DancerMedia service.
 *
 * Extracted verbatim from `dancer-media.service.ts` (behaviour-preserving
 * split). No I/O — pure input → output, safe to unit test in isolation.
 */
import type { MediaType } from './dancer-media.schema';

/**
 * Max upload size per media type — 5 MB headshot, 10 MB photo, 100 MB video.
 * Video size budget matches the public-profile uploadProfileMediaAction path
 * so the two surfaces stay consistent.
 */
export function resolveMaxUploadSize(mediaType: MediaType): number {
  return mediaType === 'headshot'
    ? 5 * 1024 * 1024
    : mediaType === 'video'
      ? 100 * 1024 * 1024
      : 10 * 1024 * 1024;
}

/**
 * MIME allowlist per media type. Note: 'image/jpg' is non-standard but sent by
 * some browsers (IE, Safari) — keep both for compatibility.
 */
export function resolveAllowedMimeTypes(mediaType: MediaType): {
  allowedTypes: string[];
  allowedDescription: string;
} {
  const imageMimeTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const videoMimeTypes = ['video/mp4', 'video/quicktime', 'video/webm'];
  return mediaType === 'video'
    ? { allowedTypes: videoMimeTypes, allowedDescription: 'MP4, MOV, WebM' }
    : { allowedTypes: imageMimeTypes, allowedDescription: 'JPEG, PNG, WebP' };
}

/**
 * Generate a storage path. Folder per media type: headshot/, photos/, videos/.
 * Path shape stays `{userId}/{folder}/{filename}` so the existing storage RLS
 * policies (split_part on '/') keep working.
 */
export function buildMediaStoragePath(
  userId: string,
  mediaType: MediaType,
  originalFileName: string,
): string {
  const timestamp = Date.now();
  const sanitizedFileName = originalFileName.replace(/[^a-zA-Z0-9.-]/g, '_');
  const fileName = `${timestamp}_${sanitizedFileName}`;
  const folder =
    mediaType === 'headshot'
      ? 'headshot'
      : mediaType === 'video'
        ? 'videos'
        : 'photos';
  return `${userId}/${folder}/${fileName}`;
}

/**
 * Validate an external video URL (YouTube / Vimeo only).
 */
export function isValidExternalVideoUrl(url: string): boolean {
  const urlPattern =
    /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be|vimeo\.com)\/.+$/i;
  return urlPattern.test(url);
}

/**
 * Normalize an S3-style storage ETag into a byte-content hash (BAL-700).
 *
 * For a single (non-multipart) PUT — which every dancer-media browser
 * direct-upload is (headshots/photos are small; even 100 MB videos PUT in
 * one request from the browser) — the ETag IS the md5 hex digest of the
 * bytes, wrapped in double quotes: `"c5e8c553235d9af30ef4f6e280790b92"`.
 *
 * A MULTIPART upload's ETag is `<hash>-<partCount>` where `<hash>` is the
 * md5 of the concatenated part digests, NOT the md5 of the content — so it
 * is NOT a usable content hash. We return null for those (and for any ETag
 * that isn't a bare 32-char hex string): the content_hash column is
 * nullable and the partial unique index only fires when it IS set, so
 * skipping is safe — we simply never key a non-content value into the
 * dedup index.
 */
export function normalizeContentHash(
  rawEtag: string | null | undefined,
): string | null {
  if (!rawEtag) return null;
  const unquoted = rawEtag.replace(/^"+|"+$/g, '').trim();
  return /^[0-9a-f]{32}$/i.test(unquoted) ? unquoted.toLowerCase() : null;
}

/**
 * Build the partial metadata update for artist_media rows. Only keys present
 * (not `undefined`) on the input are written, so a caller can clear a field to
 * null without touching the others.
 */
export function buildMediaMetadataUpdates(input: {
  title?: string | null;
  description?: string | null;
  category?: string | null;
}): {
  title?: string | null;
  description?: string | null;
  category?: string | null;
} {
  const updates: {
    title?: string | null;
    description?: string | null;
    category?: string | null;
  } = {};
  if (input.title !== undefined) updates.title = input.title;
  if (input.description !== undefined) updates.description = input.description;
  if (input.category !== undefined) updates.category = input.category;
  return updates;
}
